import React, {useCallback, useMemo} from 'react';

import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {globalXAxisOptions} from '../../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {SettingIndentedRow} from '../components/SettingIndentedRow';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {SemanticLegendSettings, SettingSelectOptionType} from '../types';
import {xAxisLabel} from '../utils';
import {DEFAULT_X_AXIS_OPTION_VALUES} from './SemanticLegendDefaults';

export type SemanticLegendMetricProps = {
  metric: string;
  updateSetting: (settings: Partial<SemanticLegendSettings>) => void;
};

export const SemanticLegendMetric = ({
  updateSetting,
  metric,
}: SemanticLegendMetricProps) => {
  const metricOptions = useWorkspaceMetricOptions();
  const metricValue = useMemo(() => {
    const isMetric = (metricOptions ?? []).some(opt => opt.value === metric);
    if (isMetric && metric != null) {
      return {
        value: metric,
        label: xAxisLabel(metric),
      };
    }
    return {
      value: '',
      label: '',
    };
  }, [metric, metricOptions]);

  const onMetricChange = useCallback(
    (selected: SettingSelectOptionType | null) => {
      if (selected != null) {
        updateSetting({
          metric: selected.value,
        });
      }
    },
    [updateSetting]
  );

  return (
    <div className="flex flex-col" data-test="metric-setting">
      <SettingIndentedRow className="gap-8">
        <div className="flex w-full">
          <SettingLabel text="Metric" className="ml-14 w-[87px]" />
          <div className="item-center flex w-full flex-row gap-8">
            <SettingSelect
              className="grow"
              options={metricOptions}
              value={metricValue}
              onChange={onMetricChange}
            />
          </div>
        </div>
      </SettingIndentedRow>
    </div>
  );
};

export const useWorkspaceMetricOptions = () => {
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const historyKeyXAxis = useMemo(
    () => globalXAxisOptions(historyKeyInfo),
    [historyKeyInfo]
  );

  const metricValues = useMemo(() => {
    return DEFAULT_X_AXIS_OPTION_VALUES.concat(historyKeyXAxis)
      .filter(val => !isSystemMetric(val))
      .map(val => ({
        label: xAxisLabel(val),
        value: val,
      }));
  }, [historyKeyXAxis]);

  return metricValues;
};
